import React from 'react';
import { StylesProps } from '@theme/jss-types';
import { RouteChildrenProps } from 'react-router';
import { HocOptions } from '@common/generic-hoc.types';
import GenericHoc from '@common/generic-hoc';
import {
  Modal, Button, Form, Input, Spin,
  message,
} from 'antd';
import paymentLinkStyles from './payment-link.styles';
import { locationChangePayment, editDetailsPayment } from '@network/dropoff.api';

interface PaymentModalProps extends
  StylesProps<ReturnType<typeof paymentLinkStyles>>, RouteChildrenProps {
  consignments: string;
  isOpen: boolean;
  isLocationChange: boolean;
  loading?: boolean;
  setIsPaymentModalOpen: (isOpen: boolean) => void;
  amountToShow: number;
  onPaymentLinkSent?: () => void | Promise<void>;
}

const PaymentModal: React.FC<PaymentModalProps> = ({
  consignments,
  classes,
  isOpen,
  isLocationChange,
  loading = false,
  setIsPaymentModalOpen,
  amountToShow,
  onPaymentLinkSent,
}) => {
  const [form] = Form.useForm();

  const [email, setEmail] = React.useState<string>('');
  const [phoneNumber, setPhoneNumber] = React.useState<string>('');
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (values.errorFields?.length) {
        return;
      }
      const apiData = {
        referenceNumber: [consignments],
        email,
        phoneNumber,
      };

      if (isLocationChange) {
        const res = await locationChangePayment(apiData);
        if (res) {
          message.success('Payment link sent successfully');
          setIsPaymentModalOpen(false);
          // Call callback if provided
          if (onPaymentLinkSent) {
            await onPaymentLinkSent();
          }
        } else if (res?.error) {
          message.error(res.error);
        }
      } else {
        const res = await editDetailsPayment(apiData);
        if (res) {
          message.success('Payment link sent successfully');
          setIsPaymentModalOpen(false);
          // Call callback if provided (for editDetails, this will recalculate weight mismatch)
          if (onPaymentLinkSent) {
            await onPaymentLinkSent();
          }
        } else if (res?.error) {
          message.error(res.error);
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        message.error(error.message);
      } else {
        message.error('An unexpected error occurred');
      }
    }
  };

  const onClose = () => {
    form.resetFields();
    setEmail('');
    setPhoneNumber('');
    setIsPaymentModalOpen(false);
  };
  const renderModalTitle = () => {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
      }}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span
            onClick={onClose}
            style={{
              fontSize: '20px',
              cursor: 'pointer',
              marginRight: '12px',
            }}
          >
            ×
          </span>
          <span style={{ fontSize: '16px', fontWeight: 500 }}>
            {isLocationChange ? 'Dropoff Location Change: Payment' : 'Edit Details: Payment'}
          </span>
        </div>
        <Button
          type="primary"
          onClick={handleSubmit}
          disabled={loading}
          style={{
            backgroundColor: '#1D4ED8',
            borderColor: '#1D4ED8',
          }}
        >
          Send Payment Link
          <Spin
            spinning={loading}
            size="small"
            style={{ color: 'white', marginLeft: '8px' }}
          />
        </Button>
      </div>
    );
  };

  return (
    <Modal
      className={classes.modal}
      open={isOpen}
      onCancel={onClose}
      title={renderModalTitle()}
      footer={null}
      destroyOnClose
      maskClosable={false}
      closeIcon={<></>}
      width={440}
      bodyStyle={{ background: '#fff' }}
      style={{
        top: 20,
      }}
    >
      {isLocationChange && (
        <div className={classes.warningNote}>
          <div className={classes.warningText}>
            You are dropping off your shipment at a different location. There&apos;s
            an additional amount of
            {' '}
            <span style={{ fontWeight: 'bold' }}>
              INR
              {' '}
              {amountToShow}
            </span>
            {' '}
            pending. Fill the details to complete payment
          </div>
        </div>
      )}
      {!isLocationChange && (
      <div className={classes.warningNote}>
        <div className={classes.warningText}>
          Additional payment in
          {' '}
          <span style={{ fontWeight: 'bold' }}>
            INR
            {' '}
            {amountToShow}
          </span>
        </div>
      </div>
      )}

      <Form form={form} layout="horizontal">
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <label style={{
              width: '140px',
              marginRight: '15px',
              fontSize: '14px',
              color: 'rgba(0, 0, 0, 0.85)',
            }}
            >
              Email :
            </label>
            <div style={{ flex: 1 }}>
              <Form.Item
                name="email"
                style={{ margin: 0 }}
                rules={[
                  { required: true, message: 'Please enter your email' },
                  { type: 'email', message: 'Please enter a valid email' },
                ]}
              >
                <Input
                  placeholder="Write Here"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </Form.Item>
            </div>
          </div>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <label style={{
              width: '140px',
              marginRight: '15px',
              fontSize: '14px',
              color: 'rgba(0, 0, 0, 0.85)',
            }}
            >
              Phone Number :
            </label>
            <div style={{ flex: 1 }}>
              <Form.Item
                name="phoneNumber"
                style={{ margin: 0 }}
                rules={[
                  { required: true, message: 'Please enter your phone number' },
                  { pattern: /^[0-9]{10}$/, message: 'Please enter a valid 10-digit phone number' },
                ]}
              >
                <Input
                  placeholder="Write Here"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                />
              </Form.Item>
            </div>
          </div>
        </div>
      </Form>

    </Modal>
  );
};

const hocConfig: HocOptions = {
  connectRedux: {
    useRedux: true,
  },
  connectJss: {
    useJss: true,
    styleSheet: paymentLinkStyles,
  },
  connectRouter: true,
};

export default GenericHoc(hocConfig)(PaymentModal);
